import { Calendar, momentLocalizer, View } from 'react-big-calendar';
import moment, { Moment } from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import React, { Children, cloneElement, useEffect, useState } from 'react';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.scss';
import MyWeek from './custom-view';
import './weekday-calendar.css';
import { useConfirmationDialog } from '../confirm-popup/confirm-popup';
import { BigCalendarEventComponent } from './big-calendar-event-component';
import {
  Appointment,
  getInvalidCustomerFieldsEnquiry,
  IShowroomShift,
  IStaffLeave,
  Lead,
  VehicleModelMinimal,
  VehicleVariantMinimal,
} from '../..';
import { IEventMapDetail } from '../../../containers/leads/components/activities/add-activity';
import { IMinMaxDate } from './calendar-mapper-utils';
import { CalendarStaffHeader } from './calendar-staff-header';
import CustomerEdit from '../../../containers/customer/components/customer-stage-edit/CustomerEdit';
import api from '../../../api';
import { useMasterData } from '../../hooks/useMasterData';
import { useCalendarContext } from '../../hooks/useCalendarContext';
import { useDataLoader } from 'react-remote-data-hooks';
import { isSameDay } from 'date-fns';
import { checkIfExcludedDatesBeBlocked } from '../../utils/event-utils';

interface IBigCalendarcomponent {
  selectedShowrooms?: string[];
  currentView: string;
  selectedDate: Date;
  onChangeDate?: (date: Date, view: 'day' | 'week' | 'month') => void;
  events: IBigCalendarEvent[];
  resourceMap: IBigCalendarResource[];
  onUpdateEvent: (event: IBigCalendarEvent, index: number) => void;
  minMax: IMinMaxDate;
  height?: string;
  tempEvent?: IBigCalendarEvent;
  skipConfirm?: boolean;
  onSlotClick?: (start: Date, resourceId: string) => void;
  staffLeave?: { [id: string]: IStaffLeave[] };
  shiftTimings?: IShowroomShift[];
  resourceHeader?: (resource: IBigCalendarResource) => JSX.Element;
  onSelectSlot?: (
    start: Date,
    end: Date,
    resourceId: string,
    isEvent: boolean
  ) => void;
  selectable?: boolean;
  onReload?: () => void;
  currentActivityType?: string;
  addActivity?:(data:any) => void,  
}
const localizer = momentLocalizer(moment);
const DragAndDropCalendar: any = withDragAndDrop(Calendar as any);

export const BigCalendarComponent: React.FunctionComponent<IBigCalendarcomponent> = (
  props
) => {
  const masterData = useMasterData(api.settings);
  const [customerModal, setCustomerModal] = React.useState<{
    isOpen?: boolean;
    id?: string;
    eventId?: string;
    leadDetails?: Lead;
  }>({});

  const { eventMarkAsDone } = useCalendarContext();
  const {
    data: excludedDatesData,
    reload: reloadExcludedDates,
  } = useDataLoader<Date[]>(() =>
    checkIfExcludedDatesBeBlocked(props.currentActivityType)
      ? api.holiday.getExcludedDatesForEvents()
      : []
  );
  const skipConfirm = props.skipConfirm ?? false;
  const mapEvents = (): IBigCalendarEvent[] => {
    const modifiedEvents: IBigCalendarEvent[] = [];
    props.events?.forEach((p) => {
      modifiedEvents.push(getMaxMinDate(p));
    });
    return modifiedEvents;
  };
  const getMaxMinDate = (event: IBigCalendarEvent) => {
    const e = { ...event };
    if (props.currentView !== 'day') {
      return e;
    }
    const startArray: Moment[] = [
      moment(e.start),
      moment(props.selectedDate).startOf('day'),
    ];
    const endArray = [moment(e.end), moment(props.selectedDate).endOf('day')];
    e.start = moment.max(startArray).toDate();
    e.end = moment.min(endArray).toDate();
    return e;
  };

  const { getConfirmation } = useConfirmationDialog();
  const today = new Date();
  const [events, setEvents] = useState(mapEvents());
  const resourceMap: IBigCalendarResource[] = props.resourceMap;
  const [scrollToTime, setScrollToTime] = useState(today);

  useEffect(() => {
    if (!props.tempEvent) {
      if (props.shiftTimings == null || props.shiftTimings.length === 0) {
        const dateArray = events?.map((d) => moment(d.start));
        const min = moment.min(dateArray).toDate();
        setScrollToTime(min);
      } else {
        const dateArray = props.shiftTimings?.map((d) => moment(d.start));
        const min = moment.min(dateArray).toDate();
        setScrollToTime(min);
      }
    } else {
      setScrollToTime(props.tempEvent?.start);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  useEffect(() => {
    let allEvents = mapEvents();
    if (props.tempEvent) {
      allEvents = allEvents.filter((p) => p.id !== props.tempEvent?.id);
      const tempEvent = getMaxMinDate(props.tempEvent);
      allEvents.push(tempEvent);
    }
    setEvents(allEvents);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.events, props.tempEvent]);

  useEffect(() => {
    reloadExcludedDates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.currentActivityType]);

  const showConfirm = () => {
    return getConfirmation({
      title: 'Would You Like To Modify Your Existing Appointment?',
    });
  };

  const moveEvent = async ({ event, start, end, resourceId }: any) => {
    const confirmed = skipConfirm || (await showConfirm());
    if (confirmed) {
      const idx = events.indexOf(event);
      if (resourceId === undefined) {
        resourceId = event.resourceId;
      }
      const updatedEvent = { ...event, start, end, resourceId };
      props.onUpdateEvent(updatedEvent, idx);
    }
  };

  const resizeEvent = async ({ event, start, end }: any) => {
    const updatedEvent = { ...event, start, end };
    const confirmed = skipConfirm || (await showConfirm());
    if (confirmed) {
      const idx = events.indexOf(event);
      props.onUpdateEvent(updatedEvent, idx);
    }
  };

  const eventStyleGetter = (
    event: IBigCalendarEvent,
    start: Date,
    end: Date,
    isSelected: boolean
  ) => {
    const hexColor = '#' + event.displayDetails.color;
    const customizedStyle = {
      backgroundColor: hexColor,
      borderRadius: '0px',
      opacity: 0.8,
      color: 'black',
      border: '0px',
      display: 'block',
      borderLeft: `3px solid rgba(0,0,0,0.15)`,
    };
    return {
      style: customizedStyle,
    };
  };

  const hasStaffLeave = (currentProps: any) => {
    if (
      !currentProps.resource ||
      props.staffLeave?.[currentProps.resource] == null ||
      props.staffLeave?.[currentProps.resource] === []
    ) {
      return false;
    }
    const date = moment(currentProps.value);
    let isBetween = false;
    props.staffLeave?.[currentProps.resource]?.forEach((p) => {
      if (date.isBetween(moment(p.startDate), moment(p.endDate))) {
        isBetween = true;
      }
    });
    return isBetween;
  };

  const hasShowroomShift = (currentProps: any) => {
    const date = moment(currentProps.value);
    let isBetween = false;
    props.shiftTimings?.forEach((p) => {
      if (date.isBetween(moment(p.start), moment(p.end))) {
        isBetween = true;
      }
    });

    return isBetween;
  };

  const ColoredDateCellWrapper = (currentProps: any) => {
    const isLeaveDay = hasStaffLeave(currentProps);
    if (isLeaveDay) {
      return cloneElement(Children.only(currentProps.children), {
        className: 'rbc-time-slot leave-slot',
      });
    }
    if (props.shiftTimings == null || props.shiftTimings.length === 0) {
      return currentProps.children;
    }
    if (!hasShowroomShift(currentProps)) {
      return cloneElement(Children.only(currentProps.children), {
        className: 'rbc-time-slot non-shift-slot',
      });
    }
    return currentProps.children;
  };

  const EmptyToolBar = (prop: any) => {
    return <div></div>;
  };

  const resourceHeaderComponent = (prop: any) => {
    const resource: IBigCalendarResource = prop.resource;
    if (props.resourceHeader) {
      return props.resourceHeader(resource);
    }

    return <CalendarStaffHeader resource={resource}></CalendarStaffHeader>;
  };

  const onSelectSlot = ({ action, start, end, resourceId }: any) => {
    if (props.onSelectSlot && action === 'select') {
      props.onSelectSlot(start, end, resourceId, false);
    }
    if (props.onSlotClick && action === 'click') {
      props.onSlotClick(start, resourceId);
    }
  };

  const onSelectEvent = (event: IBigCalendarEvent) => {
    if (props.onSelectSlot && event.displayDetails.isDraggable) {
      props.onSelectSlot(event.start, event.end, event.resourceId, true);
    }
  };
  return (
    <div
      key={
        JSON.stringify(props.staffLeave ?? {}) +
        JSON.stringify(props.shiftTimings?.map((p) => p._id) ?? {})
      }
    >
      <DragAndDropCalendar
        resizable
        min={props.minMax.min}
        max={props.minMax.max}
        components={{
          event: (data: any) => {
            return (
              <BigCalendarEventComponent
                addActivity={props.addActivity}
                currentView={props.currentView}
                event={data.event}
                setCustomerModal={(
                  id: string,
                  eventId: string,
                  leadDetails?: Lead
                ) =>
                  setCustomerModal({
                    isOpen: true,
                    id,
                    eventId,
                    leadDetails,
                  })
                }
              ></BigCalendarEventComponent>
            );
          },
          timeSlotWrapper: ColoredDateCellWrapper,
          toolbar: EmptyToolBar,
          resourceHeader: resourceHeaderComponent,
        }}
        onRangeChange={(range: any, view: any) => {
          if (props.onChangeDate) {
            props.onChangeDate(range[0], view ?? props.currentView);
          }
        }}
        allDayAccessor={false}
        localizer={localizer}
        popup={true}
        drilldownView="day"
        events={events}
        selectable={
          props.selectable
            ? !excludedDatesData?.find((d) =>
                isSameDay(new Date(d), new Date(props.selectedDate))
              )
            : props.selectable
        }
        // selectable={props.selectable}
        timeslots={4}
        step={15}
        onDrillDown={(date: Date, view: View) => {
          if (props.onChangeDate) {
            props.onChangeDate(date, view as any);
          }
        }}
        draggableAccessor={(event: IBigCalendarEvent) => {
          return event.displayDetails.isDraggable;
        }}
        // startAccessor="start"
        // endAccessor="end"
        //   endAccessor={({ end }: any) => new Date(end.getTime() - 1)}
        onSelectSlot={onSelectSlot}
        onSelectEvent={onSelectEvent}
        style={{ height: props.height ?? '78vh' }}
        onEventResize={resizeEvent}
        defaultView={props.currentView}
        date={props.selectedDate}
        onEventDrop={moveEvent}
        resources={resourceMap.length > 0 ? resourceMap : undefined}
        resourceIdAccessor={resourceMap.length > 0 ? 'resourceId' : undefined}
        resourceTitleAccessor={
          resourceMap.length > 0 ? 'resourceTitle' : undefined
        }
        scrollToTime={scrollToTime}
        eventPropGetter={eventStyleGetter}
        views={{ month: true, day: true, week: MyWeek }}
      />
      {customerModal?.isOpen && (
        <>
          <CustomerEdit
            onClose={() => setCustomerModal({})}
            id={customerModal.id || ''}
            onReload={async () => {
              const customerData = await api.customer.getDetails(
                customerModal.id!
              );

              const invalidCustomerFieldsForEnquiry = customerData
                ? getInvalidCustomerFieldsEnquiry(
                    customerData,
                    masterData?.data?.leadStages
                      ?.find((x) => x.code === 'enquiry')
                      ?.entity?.find((c) => c.key === 'Customer')!
                  )
                : [];

              if (!invalidCustomerFieldsForEnquiry?.length) {
                eventMarkAsDone(customerModal.eventId!, () => {
                  setCustomerModal({});
                  props.onReload?.();
                });
              } else {
                setCustomerModal({});
              }
            }}
            entity={
              masterData?.data?.leadStages?.find((x) => x.code === 'enquiry')
                ?.entity!
            }
            leadDetails={customerModal.leadDetails}
          />
        </>
      )}
    </div>
  );
};
export interface IBigCalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resourceId: string;
  appointment: Appointment;
  activityType: string;
  displayDetails: IEventMapDetail;
  overnightTestdriveEnabled?: boolean;
}

export interface IBigCalendarResource {
  resourceId: string;
  resourceTitle: string;
  resourceType: 'vehicle' | 'staff';
  subText?: string;
  showroom?: IShowrrom;
  avatar?: any;
  vehicleModel?: VehicleModelMinimal;
  vehicleVariant?: VehicleVariantMinimal;
  registrationNo?: string;
}

export interface IShowrrom {
  _id: string;
  title: string;
}
